import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';

import { BizyProfile, BizyProfileErrorReason } from '../schemas/bizy-profile';
import { isHttpErrorType } from '../types/error';
import { bizyEverboardingQueryKey } from './bizy-everboarding';

// ─── Query Keys ──────────────────────────────────────────────────────────────

enum QueryKey {
	Profile = 'bizy-profile',
}

export const bizyProfileQueryKey = () => [QueryKey.Profile];

// ─── Path helpers ────────────────────────────────────────────────────────────

export const bizyProfilePath = (prefix = '') => `${prefix}/bizy/profile`;

// ─── Types ───────────────────────────────────────────────────────────────────

/**
 * The PATCH body sends raw snake_case field keys exactly as advertised in
 * `required_fields`. A value of `''` or `null` clears that field server-side.
 */
export type BizyProfilePatchPayload = Record<string, string | null>;

/**
 * Parsed shape of a per-field PATCH error. The `reason` strings are stable per
 * the backend contract — safe to switch on in the UI.
 */
export interface BizyProfilePatchFieldError {
	error: string;
	field: string;
	reason: BizyProfileErrorReason;
	maxLength?: number;
}

/**
 * Extracts a per-field error payload from an HTTP error, if present. Returns
 * `null` for transport-level / unknown errors so callers can fall through to a
 * generic message.
 */
export const parseBizyProfilePatchError = (error: unknown): BizyProfilePatchFieldError | null => {
	if (!isHttpErrorType(error)) return null;
	const data = error.response?.data as Record<string, unknown> | undefined;
	if (!data || typeof data !== 'object') return null;

	const field = typeof data.field === 'string' ? data.field : null;
	const reasonResult = BizyProfileErrorReason.safeParse(data.reason);
	if (!field || !reasonResult.success) return null;

	return {
		error: typeof data.error === 'string' ? data.error : 'Failed to save',
		field,
		reason: reasonResult.data,
		maxLength: typeof data.max_length === 'number' ? data.max_length : undefined,
	};
};

// ─── Query hooks ─────────────────────────────────────────────────────────────

/**
 * Fetches the current user's Bizy profile fields.
 *
 * GET /api/v2/bizy/profile
 *
 * Note: this endpoint deliberately does NOT use the `Key-Inflection: camel`
 * header so that the snake_case keys in `required_fields` round-trip as keys
 * into both `fields` (read) and the PATCH body (write).
 */
export const useBizyProfile = (options: { enabled?: boolean } = {}) => {
	const http = useHttpClient();

	return useQuery({
		queryKey: bizyProfileQueryKey(),
		queryFn: async () => {
			const res = await http.get<Record<string, unknown>>(bizyProfilePath());
			return BizyProfile.parse(res.data);
		},
		refetchOnMount: 'always',
		enabled: options.enabled ?? true,
	});
};

// ─── Mutation hooks ──────────────────────────────────────────────────────────

/**
 * Patches one or more profile fields. Pass an object whose keys are server
 * snake_case names (matching `required_fields`). Non-blank string values set
 * (or update) the field; `null`/`''`/whitespace-only values clear it.
 *
 * PATCH /api/v2/bizy/profile
 *
 * Failure responses are surfaced as `BizyProfilePatchFieldError`:
 * - `too_long` (422): value exceeds the server max length (`maxLength` echoed back)
 * - `rejected` (422): value tripped the server's prompt-injection guard
 * - `invalid_field` (400): unknown field name in the payload
 *
 * On success this also invalidates the everboarding query because the server
 * may clear `everboarding.completed_at` if a required field transitions from
 * populated → blank.
 */
export const useBizyUpdateProfileMutation = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (fields: BizyProfilePatchPayload): Promise<BizyProfile> => {
			const res = await http.patch<Record<string, unknown>>(bizyProfilePath(), { fields });
			return BizyProfile.parse(res.data);
		},
		onSuccess: (data) => {
			queryClient.setQueryData(bizyProfileQueryKey(), data);
			queryClient.invalidateQueries(bizyEverboardingQueryKey());
		},
	});
};
